import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, PackagePlus, Search, SlidersHorizontal, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { db, limitValue, money, type PackageRecord } from "@/lib/billing";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

const emptyPackage = {
  name: "",
  slug: "",
  description: "",
  audience: "",
  monthly_price: 0,
  currency: "eur",
  is_trial: false,
  is_enterprise: false,
  is_active: true,
  is_public: true,
  is_recommended: false,
  billing_period: "monthly",
  name_fr: "",
  name_en: "",
  description_fr: "",
  description_en: "",
  audience_fr: "",
  audience_en: "",
  marketing_features_fr: [],
  marketing_features_en: [],
  cta_label_fr: "S'abonner",
  cta_label_en: "Subscribe",
  sort_order: 10,
  max_websites: 1,
  max_articles_per_month: 100,
  max_draft_articles: 25,
  max_ai_credits: 100,
  max_media_assets: 500,
  max_team_members: 1,
  storage_mb: 1000,
  bandwidth_mb: 10000,
  support_level: "basic",
  publishing_methods: ["manual"],
  features: {
    live_publishing: true,
    custom_domain: false,
    automation: false,
    wordpress_publishing: false,
    plesk_provisioning: false,
    translation: false,
    seo: true,
    api_embed: false,
  },
};

const suggestedPackages: Array<Partial<PackageRecord>> = [
  {
    name: "Trial",
    slug: "trial",
    description: "Highly restricted preview access for testing Delapresse without production publishing.",
    audience: "Visitors evaluating Delapresse",
    monthly_price: 0,
    currency: "eur",
    is_trial: true,
    is_enterprise: false,
    is_active: true,
    sort_order: 1,
    trial_days: 14,
    max_websites: 1,
    max_articles_per_month: 3,
    max_draft_articles: 3,
    max_ai_credits: 10,
    max_media_assets: 10,
    max_team_members: 1,
    storage_mb: 250,
    bandwidth_mb: 500,
    support_level: "basic_email",
    publishing_methods: ["manual_draft"],
    features: {
      live_publishing: false,
      custom_domain: false,
      automation: false,
      wordpress_publishing: false,
      plesk_provisioning: false,
      translation: false,
      seo: false,
      api_embed: false,
      watermark: true,
    },
  },
  {
    name: "News Site Starter",
    slug: "news-site-starter",
    description: "Entry plan for one small news website with manual publishing and basic managed-site support.",
    audience: "Small publishers launching one site",
    monthly_price: 49,
    currency: "eur",
    is_trial: false,
    is_enterprise: false,
    is_active: true,
    sort_order: 2,
    trial_days: null,
    max_websites: 1,
    max_articles_per_month: 100,
    max_draft_articles: 50,
    max_ai_credits: 250,
    max_media_assets: 500,
    max_team_members: 2,
    storage_mb: 5000,
    bandwidth_mb: 50000,
    support_level: "basic_hosting",
    publishing_methods: ["manual", "wordpress_basic"],
    features: {
      live_publishing: true,
      custom_domain: false,
      automation: false,
      wordpress_publishing: true,
      plesk_provisioning: false,
      translation: false,
      seo: true,
      api_embed: false,
      watermark: false,
    },
  },
  {
    name: "News Site Pro",
    slug: "news-site-pro",
    description: "Professional publishing package with scheduled publishing, AI tools, custom domains, and Plesk support.",
    audience: "Active publishers managing several sites",
    monthly_price: 149,
    currency: "eur",
    is_trial: false,
    is_enterprise: false,
    is_active: true,
    sort_order: 3,
    trial_days: null,
    max_websites: 3,
    max_articles_per_month: 1000,
    max_draft_articles: 300,
    max_ai_credits: 2500,
    max_media_assets: 3000,
    max_team_members: 5,
    storage_mb: 25000,
    bandwidth_mb: 250000,
    support_level: "standard",
    publishing_methods: ["manual", "scheduled", "wordpress", "plesk"],
    features: {
      live_publishing: true,
      custom_domain: true,
      automation: true,
      wordpress_publishing: true,
      plesk_provisioning: true,
      translation: false,
      seo: true,
      api_embed: false,
      watermark: false,
    },
  },
  {
    name: "Media Network",
    slug: "media-network",
    description: "Multi-site media network plan with full automation, smart publishing, multilingual content, and team roles.",
    audience: "Multi-site publishing teams",
    monthly_price: 499,
    currency: "eur",
    is_trial: false,
    is_enterprise: false,
    is_active: true,
    sort_order: 4,
    trial_days: null,
    max_websites: 10,
    max_articles_per_month: 5000,
    max_draft_articles: 1500,
    max_ai_credits: 15000,
    max_media_assets: 15000,
    max_team_members: 20,
    storage_mb: 100000,
    bandwidth_mb: 1000000,
    support_level: "priority",
    publishing_methods: ["manual", "scheduled", "smart", "wordpress", "plesk", "automation"],
    features: {
      live_publishing: true,
      custom_domain: true,
      automation: true,
      wordpress_publishing: true,
      plesk_provisioning: true,
      translation: true,
      seo: true,
      api_embed: true,
      watermark: false,
    },
  },
  {
    name: "Enterprise",
    slug: "enterprise",
    description: "Custom Delapresse package for dedicated infrastructure, integrations, SLA, and manual approval workflows.",
    audience: "Enterprise publishers and custom deployments",
    monthly_price: 0,
    currency: "eur",
    is_trial: false,
    is_enterprise: true,
    is_active: true,
    sort_order: 5,
    trial_days: null,
    max_websites: null,
    max_articles_per_month: null,
    max_draft_articles: null,
    max_ai_credits: null,
    max_media_assets: null,
    max_team_members: null,
    storage_mb: null,
    bandwidth_mb: null,
    support_level: "sla",
    publishing_methods: ["custom"],
    features: {
      live_publishing: true,
      custom_domain: true,
      automation: true,
      wordpress_publishing: true,
      plesk_provisioning: true,
      translation: true,
      seo: true,
      api_embed: true,
      watermark: false,
      manual_approval_required: true,
    },
  },
];

const suggestedMarketing: Record<string, Partial<PackageRecord>> = {
  trial: {
    name_fr: "Essai",
    name_en: "Trial",
    description_fr: "Accès de prévisualisation très limité pour tester Delapresse sans publication en production.",
    description_en: "Highly restricted preview access for testing Delapresse without production publishing.",
    audience_fr: "Visiteurs qui évaluent Delapresse",
    audience_en: "Visitors evaluating Delapresse",
    marketing_features_fr: ["Brouillons limités", "Crédits IA de découverte", "Médiathèque de test"],
    marketing_features_en: ["Limited drafts", "Starter AI credits", "Test media library"],
    cta_label_fr: "Tester",
    cta_label_en: "Start trial",
    is_public: true,
    is_recommended: false,
    billing_period: "monthly",
  },
  "news-site-starter": {
    name_fr: "Site News Starter",
    name_en: "News Site Starter",
    description_fr: "Offre d'entrée pour un site d'actualité avec publication manuelle et support de site basique.",
    description_en: "Entry plan for one news website with manual publishing and basic managed-site support.",
    audience_fr: "Petits éditeurs qui lancent un site",
    audience_en: "Small publishers launching one site",
    marketing_features_fr: ["Publication multi-site basique", "SEO inclus", "Sans filigrane"],
    marketing_features_en: ["Basic multi-site publishing", "SEO included", "No watermark"],
    cta_label_fr: "S'abonner",
    cta_label_en: "Subscribe",
    is_public: true,
    is_recommended: false,
    billing_period: "monthly",
  },
  "news-site-pro": {
    name_fr: "Site News Pro",
    name_en: "News Site Pro",
    description_fr: "Package professionnel avec publication programmée, IA, domaines personnalisés et support Plesk.",
    description_en: "Professional package with scheduled publishing, AI tools, custom domains, and Plesk support.",
    audience_fr: "Éditeurs actifs qui gèrent plusieurs sites",
    audience_en: "Active publishers managing several sites",
    marketing_features_fr: ["Domaines personnalisés", "Publication programmée", "Provisionnement Plesk"],
    marketing_features_en: ["Custom domains", "Scheduled publishing", "Plesk provisioning"],
    cta_label_fr: "Choisir Pro",
    cta_label_en: "Choose Pro",
    is_public: true,
    is_recommended: true,
    billing_period: "monthly",
  },
  "media-network": {
    name_fr: "Réseau Média",
    name_en: "Media Network",
    description_fr: "Plan multi-sites avec automatisation complète, publication intelligente, contenu multilingue et rôles d'équipe.",
    description_en: "Multi-site media network plan with full automation, smart publishing, multilingual content, and team roles.",
    audience_fr: "Équipes de publication multi-sites",
    audience_en: "Multi-site publishing teams",
    marketing_features_fr: ["Automatisation complète", "Traduction", "API et intégrations"],
    marketing_features_en: ["Full automation", "Translation", "API and integrations"],
    cta_label_fr: "Développer le réseau",
    cta_label_en: "Scale the network",
    is_public: true,
    is_recommended: false,
    billing_period: "monthly",
  },
  enterprise: {
    name_fr: "Entreprise",
    name_en: "Enterprise",
    description_fr: "Package Delapresse sur mesure pour infrastructure dédiée, intégrations, SLA et validations manuelles.",
    description_en: "Custom Delapresse package for dedicated infrastructure, integrations, SLA, and approval workflows.",
    audience_fr: "Groupes média et déploiements personnalisés",
    audience_en: "Enterprise publishers and custom deployments",
    marketing_features_fr: ["SLA dédié", "Infrastructure personnalisée", "Intégrations avancées"],
    marketing_features_en: ["Dedicated SLA", "Custom infrastructure", "Advanced integrations"],
    cta_label_fr: "Contacter l'équipe",
    cta_label_en: "Contact the team",
    is_public: true,
    is_recommended: false,
    billing_period: "monthly",
  },
};

async function fetchPackages(): Promise<PackageRecord[]> {
  const { data, error } = await db.from("packages").select("*").order("sort_order");
  if (error) throw error;
  return data ?? [];
}

export default function AdminPackagesPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(toLocale(i18n.language));
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<PackageRecord> | null>(null);
  const { data: packages = [], isLoading } = useQuery({ queryKey: ["admin-packages"], queryFn: fetchPackages });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return packages.filter((pkg) => [pkg.name, pkg.slug, pkg.description ?? ""].join(" ").toLowerCase().includes(q));
  }, [packages, search]);

  const savePackage = useMutation({
    mutationFn: async (pkg: Partial<PackageRecord>) => {
      const payload = {
        ...pkg,
        slug: pkg.slug || pkg.name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      };
      const { error } = pkg.id
        ? await db.from("packages").update(payload).eq("id", pkg.id)
        : await db.from("packages").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
      setEditing(null);
      toast.success(t("admin.savePackage"));
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not save package"),
  });

  const seedSuggestedPackages = useMutation({
    mutationFn: async () => {
      const existingBySlug = new Map(packages.map((pkg) => [pkg.slug, pkg]));
      const payload = suggestedPackages.map((suggested) => {
        const existing = existingBySlug.get(suggested.slug ?? "");
        return {
          ...suggested,
          ...(suggestedMarketing[suggested.slug ?? ""] ?? {}),
          stripe_product_id: existing?.stripe_product_id ?? null,
          stripe_price_id: existing?.stripe_price_id ?? null,
        };
      });
      const { error } = await db.from("packages").upsert(payload, { onConflict: "slug" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-packages"] });
      toast.success(t("admin.suggestedPackages"));
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not create suggested packages"),
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t("admin.packagesTitle")}</h1>
          <p className="text-xs text-muted-foreground">{t("admin.packagesSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => seedSuggestedPackages.mutate()} disabled={seedSuggestedPackages.isPending}>
            {seedSuggestedPackages.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {t("admin.suggestedPackages")}
          </Button>
          <Button size="sm" onClick={() => setEditing(emptyPackage)}><PackagePlus className="mr-2 h-4 w-4" />{t("admin.createPackage")}</Button>
        </div>
      </header>

      <main className="grid gap-6 p-6 xl:grid-cols-[1fr_420px]">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">{t("admin.packageCatalog")}</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder={t("admin.searchPackages")} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.packagesTitle")}</TableHead>
                    <TableHead>{t("admin.price")}</TableHead>
                    <TableHead>{t("admin.limits")}</TableHead>
                    <TableHead>{t("admin.stripe")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((pkg) => (
                    <TableRow key={pkg.id}>
                      <TableCell>
                        <div className="font-medium">{pkg.name}</div>
                        <div className="text-xs text-muted-foreground">{pkg.slug}</div>
                      </TableCell>
                      <TableCell>{pkg.is_enterprise ? t("packages.custom") : money(pkg.monthly_price, pkg.currency, false, locale)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {limitValue(pkg.max_websites)} sites, {limitValue(pkg.max_articles_per_month)} articles
                      </TableCell>
                      <TableCell>
                        {pkg.stripe_price_id ? <Badge variant="outline" className="bg-success/10 text-success"><Check className="mr-1 h-3 w-3" />{t("admin.mapped")}</Badge> : <Badge variant="outline">{t("admin.missing")}</Badge>}
                      </TableCell>
                      <TableCell><Badge variant={pkg.is_active ? "default" : "secondary"}>{pkg.is_active ? t("common.enabled") : t("common.disabled")}</Badge></TableCell>
                      <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setEditing(pkg)}><SlidersHorizontal className="mr-2 h-4 w-4" />{t("common.edit")}</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <PackageEditor value={editing} onChange={setEditing} onSave={(pkg) => savePackage.mutate(pkg)} saving={savePackage.isPending} />
      </main>
    </div>
  );
}

function PackageEditor({ value, onChange, onSave, saving }: { value: Partial<PackageRecord> | null; onChange: (value: Partial<PackageRecord> | null) => void; onSave: (value: Partial<PackageRecord>) => void; saving: boolean }) {
  const { t } = useTranslation();
  if (!value) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">{t("admin.selectPackage")}</CardContent>
      </Card>
    );
  }

  const setValue = (patch: Partial<PackageRecord>) => onChange({ ...value, ...patch });
  const setFeature = (key: string, enabled: boolean) => setValue({ features: { ...(value.features ?? {}), [key]: enabled } });
  const setList = (key: "marketing_features_fr" | "marketing_features_en", text: string) =>
    setValue({ [key]: text.split("\n").map((line) => line.trim()).filter(Boolean) } as Partial<PackageRecord>);

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{value.id ? t("admin.editPackage") : t("admin.newPackage")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("common.name")}><Input value={value.name ?? ""} onChange={(e) => setValue({ name: e.target.value })} /></Field>
          <Field label="Slug"><Input value={value.slug ?? ""} onChange={(e) => setValue({ slug: e.target.value })} /></Field>
        </div>
        <Field label={t("common.description")}><Textarea value={value.description ?? ""} onChange={(e) => setValue({ description: e.target.value })} /></Field>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Nom FR"><Input value={value.name_fr ?? ""} onChange={(e) => setValue({ name_fr: e.target.value })} /></Field>
          <Field label="Name EN"><Input value={value.name_en ?? ""} onChange={(e) => setValue({ name_en: e.target.value })} /></Field>
          <Field label="Description FR"><Textarea value={value.description_fr ?? ""} onChange={(e) => setValue({ description_fr: e.target.value })} /></Field>
          <Field label="Description EN"><Textarea value={value.description_en ?? ""} onChange={(e) => setValue({ description_en: e.target.value })} /></Field>
          <Field label="Audience FR"><Input value={value.audience_fr ?? ""} onChange={(e) => setValue({ audience_fr: e.target.value })} /></Field>
          <Field label="Audience EN"><Input value={value.audience_en ?? ""} onChange={(e) => setValue({ audience_en: e.target.value })} /></Field>
          <Field label="CTA FR"><Input value={value.cta_label_fr ?? ""} onChange={(e) => setValue({ cta_label_fr: e.target.value })} /></Field>
          <Field label="CTA EN"><Input value={value.cta_label_en ?? ""} onChange={(e) => setValue({ cta_label_en: e.target.value })} /></Field>
          <Field label="Features FR"><Textarea value={(value.marketing_features_fr ?? []).join("\n")} onChange={(e) => setList("marketing_features_fr", e.target.value)} /></Field>
          <Field label="Features EN"><Textarea value={(value.marketing_features_en ?? []).join("\n")} onChange={(e) => setList("marketing_features_en", e.target.value)} /></Field>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("admin.price")}><Input type="number" value={value.monthly_price ?? 0} onChange={(e) => setValue({ monthly_price: Number(e.target.value) })} /></Field>
          <Field label={t("packages.limits.support")}><Input value={value.support_level ?? ""} onChange={(e) => setValue({ support_level: e.target.value })} /></Field>
          <Field label="Billing period"><Input value={value.billing_period ?? "monthly"} onChange={(e) => setValue({ billing_period: e.target.value })} /></Field>
          <Field label="Stripe product ID"><Input value={value.stripe_product_id ?? ""} onChange={(e) => setValue({ stripe_product_id: e.target.value })} /></Field>
          <Field label="Stripe price ID"><Input value={value.stripe_price_id ?? ""} onChange={(e) => setValue({ stripe_price_id: e.target.value })} /></Field>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t("packages.limits.websites")}><Input type="number" value={value.max_websites ?? ""} onChange={(e) => setValue({ max_websites: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
          <Field label={t("packages.limits.articles")}><Input type="number" value={value.max_articles_per_month ?? ""} onChange={(e) => setValue({ max_articles_per_month: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
          <Field label="Draft articles"><Input type="number" value={value.max_draft_articles ?? ""} onChange={(e) => setValue({ max_draft_articles: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
          <Field label={t("packages.limits.aiCredits")}><Input type="number" value={value.max_ai_credits ?? ""} onChange={(e) => setValue({ max_ai_credits: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
          <Field label={t("packages.limits.media")}><Input type="number" value={value.max_media_assets ?? ""} onChange={(e) => setValue({ max_media_assets: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
          <Field label="Team members"><Input type="number" value={value.max_team_members ?? ""} onChange={(e) => setValue({ max_team_members: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {["live_publishing", "custom_domain", "automation", "wordpress_publishing", "plesk_provisioning", "translation", "seo", "api_embed"].map((key) => (
            <div key={key} className="flex items-center justify-between rounded-md border border-border p-3">
              <Label className="capitalize">{key.replaceAll("_", " ")}</Label>
              <Switch checked={Boolean(value.features?.[key])} onCheckedChange={(checked) => setFeature(key, checked)} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <Label>{t("admin.enabled")}</Label>
          <Switch checked={Boolean(value.is_active)} onCheckedChange={(checked) => setValue({ is_active: checked })} />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>{t("admin.public")}</Label>
            <Switch checked={(value as any).is_public !== false} onCheckedChange={(checked) => setValue({ is_public: checked } as Partial<PackageRecord>)} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <Label>{t("admin.recommended")}</Label>
            <Switch checked={Boolean((value as any).is_recommended)} onCheckedChange={(checked) => setValue({ is_recommended: checked } as Partial<PackageRecord>)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => onSave(value)} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("admin.savePackage")}</Button>
          <Button variant="outline" onClick={() => onChange(null)}>{t("common.cancel")}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
